import React from 'react';
import { Receipt, Calendar, Tag, MoreVertical, PlusCircle } from 'lucide-react';

interface Expense {
  id: string;
  date: string;
  description: string;
  category: 'materials' | 'tools' | 'vehicle' | 'insurance' | 'other';
  amount: number;
  taxDeductible: boolean;
}

const mockExpenses: Expense[] = [
  {
    id: '1',
    date: '2024-03-18',
    description: 'Power Tools Set',
    category: 'tools',
    amount: 899.99,
    taxDeductible: true,
  },
  {
    id: '2',
    date: '2024-03-17',
    description: 'Fuel for Work Van',
    category: 'vehicle',
    amount: 120.5,
    taxDeductible: true,
  },
];

const categoryColors = {
  materials: 'bg-blue-100 text-blue-800',
  tools: 'bg-purple-100 text-purple-800',
  vehicle: 'bg-green-100 text-green-800',
  insurance: 'bg-yellow-100 text-yellow-800',
  other: 'bg-gray-100 text-gray-800',
};

export function ExpenseTracker() {
  const totalExpenses = mockExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  const taxDeductible = mockExpenses
    .filter(expense => expense.taxDeductible)
    .reduce((sum, expense) => sum + expense.amount, 0);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Expenses</h2>
          <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2">
            <PlusCircle className="h-4 w-4" />
            Add Expense
          </button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Total Expenses</p>
            <p className="text-2xl font-semibold text-gray-900">${totalExpenses.toFixed(2)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500">Tax Deductible</p>
            <p className="text-2xl font-semibold text-gray-900">${taxDeductible.toFixed(2)}</p>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {mockExpenses.map(expense => (
            <div
              key={expense.id}
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center space-x-4">
                <Receipt className="h-8 w-8 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{expense.description}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-500">
                        {new Date(expense.date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Tag className="h-4 w-4 text-gray-400 mr-1" />
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${categoryColors[expense.category]}`}
                      >
                        {expense.category.charAt(0).toUpperCase() + expense.category.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-lg font-semibold text-gray-900">
                  ${expense.amount.toFixed(2)}
                </span>
                <button className="text-gray-400 hover:text-gray-600">
                  <MoreVertical className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
